import { useSyncExternalStore } from "react";

/**
 * Small HUD state that is not world state: whether the settings sheet is open, and the on-screen
 * helpers the player may switch off (minimap, compass, text labels next to the mode icons). The
 * switches are a per-device convenience, so `localStorage`; reads never throw.
 */
export interface HudPrefs {
  minimap: boolean;
  compass: boolean;
  modeLabels: boolean;
}

const KEY = "uz.world.hud";
export const DEFAULT_HUD: HudPrefs = { minimap: true, compass: true, modeLabels: false };

function read(): HudPrefs {
  try {
    const raw = JSON.parse(globalThis.localStorage?.getItem(KEY) ?? "null") as Partial<HudPrefs>;
    if (!raw || typeof raw !== "object") return DEFAULT_HUD;
    return {
      minimap: raw.minimap !== false,
      compass: raw.compass !== false,
      modeLabels: raw.modeLabels === true,
    };
  } catch {
    return DEFAULT_HUD;
  }
}

interface HudUi {
  settingsOpen: boolean;
  prefs: HudPrefs;
}

let state: HudUi = { settingsOpen: false, prefs: read() };
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export const hudUi = {
  getState: (): HudUi => state,
  subscribe: (l: () => void): (() => void) => {
    listeners.add(l);
    return () => listeners.delete(l);
  },
  openSettings: (open = true): void => {
    if (state.settingsOpen === open) return;
    state = { ...state, settingsOpen: open };
    emit();
  },
  toggleSettings: (): void => {
    hudUi.openSettings(!state.settingsOpen);
  },
  setPrefs: (patch: Partial<HudPrefs>): void => {
    state = { ...state, prefs: { ...state.prefs, ...patch } };
    try {
      globalThis.localStorage?.setItem(KEY, JSON.stringify(state.prefs));
    } catch {
      // Private mode: kept for this session only.
    }
    emit();
  },
};

export function useHudUi(): HudUi {
  return useSyncExternalStore(hudUi.subscribe, hudUi.getState, hudUi.getState);
}
